import type { FxEvent } from './types'
import { drawWashLitBody, type WashVariant } from './parry_wash_fx'
import { blurPx } from './render_scale'

export type BloomShape = 'a' | 'b' | 'c'
type Spark = Extract<FxEvent, { kind: 'parry_spark' }>
export const BLOOM = {
  cloudKeys: [0, 0.04, 0.13, 0.24], cloudLife: 0.36,
  emberKeys: [0, 0.06, 0.18, 0.32], emberLife: 0.48,
  normalHeight: 110, justHeight: 150, normalRadius: 120, justRadius: 150,
  rimLife: 0.30, silverFilter: 'saturate(0.15) brightness(1.05)',
} as const

export function createBloomShapeChooser(): (event: object) => BloomShape {
  const shapes = new WeakMap<object, BloomShape>()
  let next = 0
  return event => {
    let shape = shapes.get(event)
    if (!shape) { shape = (['a', 'b', 'c'] as const)[next++ % 3]; shapes.set(event, shape) }
    return shape
  }
}
export const bloomShapeFor = createBloomShapeChooser()
export function bloomScale(intensity = 1): number {
  return 1 + 0.15 * (Math.max(1, Math.min(2, intensity)) - 1)
}
export function bloomRimEnvelope(age: number): number {
  return age < 0 || age >= BLOOM.rimLife ? 0 : age < 0.02 ? 1 : Math.max(0, 1 - (age - 0.02) / 0.28)
}
export function bloomZoom(age: number, just: boolean): number {
  if (age < 0) return 1
  const zk = age < 0.03 ? age / 0.03 : Math.max(0, 1 - (age - 0.03) / 0.22)
  return 1 + (just ? 0.07 : 0.04) * zk * 0.35
}
// 普段のパリィの光の見た目 (配布版は常に sa0)。DEV だけ ?parryFlash=w1|w2|w3|sa0|old と
//   window.__setParryFlash('w2') で切り替える (検収パック kensyu-parry-light.html が使う・2026-10-08)。
//   sa0 = 光の雲＋火の粉＋体の縁 (今の既定) / old = 前の drawParrySpark / w1〜w3 = 光の雲なしで体を照らす＋線の火花 (src/parry_wash_fx.ts)。
export type ParryFlashMode = 'sa0' | 'old' | 'w1' | 'w2' | 'w3' | 'w2s' | 'w2x'
export const PARRY_FLASH_MODES: readonly ParryFlashMode[] = ['sa0', 'old', 'w1', 'w2', 'w3', 'w2s', 'w2x']
export function parseParryFlashMode(v: unknown): ParryFlashMode | null {
  const k = typeof v === 'string' ? v.toLowerCase() : ''
  return (PARRY_FLASH_MODES as readonly string[]).includes(k) ? k as ParryFlashMode : null
}
let mode: ParryFlashMode = 'sa0'
if (import.meta.env.DEV && typeof window !== 'undefined') {
  mode = parseParryFlashMode(new URLSearchParams(window.location.search).get('parryFlash')) ?? 'sa0'
  // 引数なしで今の見た目を返す。知らない値は無視して今の見た目を返す。
  ;(window as any).__setParryFlash = (next?: unknown): ParryFlashMode => { mode = parseParryFlashMode(next) ?? mode; return mode }
}
export function parryFlashMode(): ParryFlashMode { return mode }
export function useOldParryFlash(): boolean { return mode === 'old' }
/** W 系なら案の名前、それ以外は null。 */
export function parryWashVariant(m: ParryFlashMode = mode): WashVariant | null {
  return m === 'w1' ? 'W1' : m === 'w2' ? 'W2' : m === 'w3' ? 'W3' : m === 'w2s' ? 'W2S' : m === 'w2x' ? 'W2X' : null
}

const frames: Record<string, HTMLImageElement> = {}
export function preloadParryBloom(prefix = '/sprites/fx/parry-family/bloom/'): void {
  if (typeof Image === 'undefined') return
  for (const base of ['bloom_a', 'bloom_b', 'bloom_c', 'embers']) {
    for (let i = 0; i < 4; i++) {
      const name = `${base}_${i}`
      if (!frames[name]) { const image = new Image(); image.src = `${prefix}${name}.webp`; frames[name] = image }
    }
  }
}
export function parryBloomReady(): boolean {
  return Object.keys(frames).length === 16 && Object.values(frames).every(f => f.complete && f.naturalWidth > 0)
}
function sequence(ctx: CanvasRenderingContext2D, base: string, age: number, keys: readonly number[], life: number,
  x: number, y: number, height: number, rotation: number, flip: boolean, just: boolean, alpha: number): void {
  if (age < 0 || age >= life) return
  ctx.save(); ctx.globalCompositeOperation = 'lighter'
  if (!just) ctx.filter = BLOOM.silverFilter
  ctx.translate(x, y); ctx.rotate(rotation); if (flip) ctx.scale(-1, 1)
  for (let i = 0; i < 4; i++) {
    const start = i === 0 ? -0.001 : keys[i - 1], peak = keys[i], end = keys[i + 1] ?? life
    const weight = age <= peak ? (i === 0 ? 1 : (age - start) / (peak - start)) : 1 - (age - peak) / (end - peak)
    if (age < start || age > end || weight <= 0.003) continue
    const f = frames[`${base}_${i}`], width = f.naturalWidth * height / f.naturalHeight
    ctx.globalAlpha = Math.min(1, weight) * alpha
    ctx.drawImage(f, -width / 2, -height / 2, width, height)
  }
  ctx.restore()
}
/** Owner FX lives 0.5 seconds in game.ts, covering the 0.48 second ember tail. */
export function drawParryBloom(ctx: CanvasRenderingContext2D, x: number, y: number, age: number,
  intensity: number, just: boolean, shape: BloomShape): boolean {
  if (mode !== 'sa0' || !parryBloomReady()) return false
  const h = (just ? BLOOM.justHeight : BLOOM.normalHeight) * bloomScale(intensity)
  sequence(ctx, `bloom_${shape}`, age, BLOOM.cloudKeys, BLOOM.cloudLife, x, y, h,
    ({ a: -12, b: 8, c: -4 }[shape]) * Math.PI / 180, shape === 'c', just, 1)
  sequence(ctx, 'embers', age, BLOOM.emberKeys, BLOOM.emberLife, x, y, h * 1.35, 0, shape === 'b', just, 0.9)
  return true
}
export function activeParryBloom(fx: FxEvent[], now: number): Spark | undefined {
  // W 系は光の雲の素材を使わないので、素材の読み込みを待たない。
  if (mode === 'old' || (mode === 'sa0' && !parryBloomReady())) return undefined
  // Register in event order, even when a later event replaces the active rim/zoom.
  let active: Spark | undefined
  for (const f of fx) if (f.kind === 'parry_spark') {
    bloomShapeFor(f)
    if (now >= f.t && now - f.t < BLOOM.rimLife && (!active || f.t >= active.t)) active = f
  }
  return active
}

let bodyCanvas: HTMLCanvasElement | undefined
let rimCanvas: HTMLCanvasElement | undefined
/** Draw the body once, preserving its pose, visibility and transform; light only its alpha silhouette. */
export function drawBloomLitBody(ctx: CanvasRenderingContext2D, spark: Spark | undefined, now: number,
  bodyX: number, bodyY: number, drawBody: (target: CanvasRenderingContext2D) => void, footY = bodyY + 60): void {
  if (!spark) { drawBody(ctx); return }
  const wash = parryWashVariant()
  if (wash) {
    drawWashLitBody(ctx, spark, now, wash, bloomScale(spark.intensity), bodyX, bodyY, footY, drawBody)
    return
  }
  bodyCanvas ??= document.createElement('canvas'); rimCanvas ??= document.createElement('canvas')
  for (const c of [bodyCanvas, rimCanvas]) {
    if (c.width !== ctx.canvas.width) c.width = ctx.canvas.width
    if (c.height !== ctx.canvas.height) c.height = ctx.canvas.height
  }
  const b = bodyCanvas.getContext('2d')!, r = rimCanvas.getContext('2d')!, m = ctx.getTransform()
  const w = bodyCanvas.width, h = bodyCanvas.height
  b.resetTransform(); b.clearRect(0, 0, w, h); b.save(); b.setTransform(m); drawBody(b); b.restore()
  ctx.save(); ctx.resetTransform(); ctx.drawImage(bodyCanvas, 0, 0); ctx.restore()
  const d = Math.hypot(spark.x - bodyX, spark.y - bodyY) || 1
  const dx = 3 * (spark.x - bodyX) / d, dy = 3 * (spark.y - bodyY) / d
  r.resetTransform(); r.clearRect(0, 0, w, h); r.globalCompositeOperation = 'source-over'
  // Keep the inside edge facing the contact: subtract a copy moved AWAY
  // from the light. A partial subtraction leaves a whole-body selection halo.
  r.drawImage(bodyCanvas, 0, 0)
  r.globalCompositeOperation = 'destination-out'
  r.drawImage(bodyCanvas, -(m.a * dx + m.c * dy), -(m.b * dx + m.d * dy))
  r.globalCompositeOperation = 'source-in'; r.fillStyle = spark.isJust ? 'rgb(255,186,90)' : 'rgb(215,225,255)'; r.fillRect(0, 0, w, h)
  r.globalCompositeOperation = 'destination-in'
  const radius = (spark.isJust ? BLOOM.justRadius : BLOOM.normalRadius) * bloomScale(spark.intensity)
  // Both silhouette and mask are in device coordinates. The same world ->
  // device matrix used by the cloud includes camera translation and bloom zoom.
  const contact = m.transformPoint({ x: spark.x, y: spark.y })
  const center = m.transformPoint({ x: bodyX, y: bodyY })
  const screenRadius = radius * Math.hypot(m.a, m.b)
  const gradient = r.createRadialGradient(contact.x, contact.y, 0, contact.x, contact.y, screenRadius)
  for (let i = 0; i <= 16; i++) gradient.addColorStop(i / 16, `rgba(255,255,255,${Math.pow(1 - i / 16, 1.6)})`)
  r.fillStyle = gradient
  // Full buffer mask with transparent gradient exterior, never a screen color wash.
  r.fillRect(0, 0, w, h)
  // Reject the far half of each body, including concave cape/leg edges that a
  // translated silhouette alone can expose. Feather only the center boundary.
  const nx = (contact.x - center.x) / (Math.hypot(contact.x - center.x, contact.y - center.y) || 1)
  const ny = (contact.y - center.y) / (Math.hypot(contact.x - center.x, contact.y - center.y) || 1)
  const facing = r.createLinearGradient(center.x - nx * 3, center.y - ny * 3, center.x + nx * 3, center.y + ny * 3)
  facing.addColorStop(0, 'rgba(255,255,255,0)'); facing.addColorStop(1, 'rgba(255,255,255,1)')
  r.fillStyle = facing; r.fillRect(0, 0, w, h); r.globalCompositeOperation = 'source-over'
  ctx.save(); ctx.resetTransform(); ctx.globalCompositeOperation = 'lighter'
  // Blur must not reintroduce the rejected far-side outline. Clip its final
  // composite to the contact-facing half-plane (device coordinates as above).
  const extent = w + h
  ctx.beginPath()
  ctx.moveTo(center.x - ny * extent, center.y + nx * extent)
  ctx.lineTo(center.x + ny * extent, center.y - nx * extent)
  ctx.lineTo(center.x + ny * extent + nx * extent, center.y - nx * extent + ny * extent)
  ctx.lineTo(center.x - ny * extent + nx * extent, center.y + nx * extent + ny * extent)
  ctx.closePath(); ctx.clip()
  const envelope = bloomRimEnvelope(now - spark.t)
  ctx.globalAlpha = envelope; ctx.filter = `blur(${blurPx(1.5)}px)`
  ctx.drawImage(rimCanvas, 0, 0)
  ctx.globalAlpha = 0.4 * envelope; ctx.drawImage(rimCanvas, 0, 0); ctx.restore()
}
